import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { approveCustomer, fetchCustomerApprovalOptions } from "../../../services/api/admin";
import { showToast } from "../../../store/toast/toastSlice";

const ApproveCustomerModal = ({ open, onClose, customer, onApproved }) => {
  const dispatch = useDispatch();
  const [branchId, setBranchId] = useState(customer?.branchId || "");
  const [teamId, setTeamId] = useState(customer?.teamId || "");
  const [secondaryTeamId, setSecondaryTeamId] = useState(customer?.secondaryTeamId || "");

  const { data: options } = useQuery({
    queryKey: ["customerApprovalOptions", customer?.id],
    queryFn: () => fetchCustomerApprovalOptions(customer?.id),
    enabled: open && !!customer?.id,
  });

  const toOptions = (rows) => (rows || []).map((row) => ({ label: row.name, value: row.id }));
  const branchOptions = toOptions(options?.branches);
  const teamOptions = toOptions(options?.serviceDeliveryTeams);
  const secondaryTeamOptions = toOptions(options?.operationsTeams);

  const { isPending: isApproving, mutateAsync: approveMutation } = useMutation({
    mutationFn: approveCustomer,
  });

  const handleApprove = async () => {
    if (!branchId || !teamId || !secondaryTeamId) {
      dispatch(
        showToast({
          message: "Select a branch, a service delivery team and an operations team.",
          type: "error",
        }),
      );
      return;
    }

    try {
      await approveMutation({ id: customer?.id, branchId, teamId, secondaryTeamId });
      dispatch(showToast({ message: "Customer approved successfully", type: "success" }));
      onApproved?.();
      onClose();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to approve customer",
          type: "error",
        }),
      );
    }
  };

  return (
    <BaseConfirmModal
      open={open}
      onClose={onClose}
      onConfirm={handleApprove}
      isLoading={isApproving}
      confirmText="Approve"
      confirmButtonColor="primary"
      title={`Are you sure you want to approve ${customer?.clientName || "this customer"}?`}
      description={
        <>
          <span className="block mb-4">Select the branch and teams to link to the customer.</span>

          <span className="grid grid-cols-1 gap-4 text-left">
            <BaseAutoSelect
              required={true}
              labelShrink={false}
              label="Branch Name"
              name="branchId"
              placeholder="Select Branch"
              options={branchOptions}
              value={branchId}
              onChange={(option) => setBranchId(option.target.value)}
              isClearable
            />
            <BaseAutoSelect
              required={true}
              labelShrink={false}
              label="Service Delivery Team"
              name="teamId"
              placeholder="Select Service Delivery Team"
              options={teamOptions}
              value={teamId}
              onChange={(option) => setTeamId(option.target.value)}
              isClearable
            />
            <BaseAutoSelect
              required={true}
              labelShrink={false}
              label="Operations Team"
              name="secondaryTeamId"
              placeholder="Select Operations Team"
              options={secondaryTeamOptions}
              value={secondaryTeamId}
              onChange={(option) => setSecondaryTeamId(option.target.value)}
              isClearable
            />
          </span>
        </>
      }
    />
  );
};

export default ApproveCustomerModal;
